import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import apiHandler from "../../handlers/ApiHandler";
import regionStore from "../../stores/RegionStore";
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Menu,
  TableSortLabel,
  InputAdornment,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FiSearch, FiFilter } from "react-icons/fi";
import { MoreVert } from "@mui/icons-material";
import { FiLink, FiCalendar, FiClock, FiBell } from "react-icons/fi";
import Fuse from "fuse.js";
import safetyRatLogo from "../../assets/safetyRatlogo.png";
import RightDrawer from "../RightDrawer";
import AddRegionModal from "../admin-dashboard-components/AddRegionModal";

// Mock inventory data
const mockData = [
  {
    id: 1,
    unitType: "E3-TEST-UNIT",
    unitName: "E3 Test Unit A",
    region: "None",
    customer: "None",
    eAndP: "None",
    status: "disconnected",
    manufacturer: "Digi",
    healthStatus: "NA",
    signalStrength: "NA",
  },
  {
    id: 2,
    unitType: "E3-TEST-UNIT",
    unitName: "E3 Test Unit B",
    region: "US",
    customer: "None",
    eAndP: "None",
    status: "disconnected",
    manufacturer: "Digi",
    healthStatus: "NA",
    signalStrength: "NA",
  },
  {
    id: 3,
    unitType: "E3-TEST-UNIT",
    unitName: "E3 Test Unit C",
    region: "UK",
    customer: "None",
    eAndP: "None",
    status: "disconnected",
    manufacturer: "Digi",
    healthStatus: "NA",
    signalStrength: "NA",
  },
];

const columns = [
  { id: "unitType", label: "Unit Type" },
  { id: "unitName", label: "Unit Name" },
  { id: "region", label: "Region" },
  { id: "customer", label: "Customer" },
  { id: "eAndP", label: "E & P" },
  { id: "status", label: "Status" },
  { id: "manufacturer", label: "Manufacturer" },
  { id: "healthStatus", label: "Health Status" },
  { id: "signalStrength", label: "Signal Strength" },
];

const TOTAL_PAGES = 10;

const InventoryComponent = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [regionList, setRegionList] = useState([]);
  const [managers, setManagers] = useState([]);

  useEffect(() => {
    const fetchRegionsAndManagers = async () => {
      try {
        const allRegions = await regionStore.regions;
        console.log("Here is all region: ", allRegions);
        setRegionList(allRegions);

        const allManager = await apiHandler.get("/api/regions/managers");
        console.log("Here is all manager: ", allManager);
        setManagers(allManager);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchRegionsAndManagers();
  }, []);

  const [suggestion, setSuggestion] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState(mockData);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isInventoryDrawerOpen, setIsInventoryDrawerOpen] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // Add state for region modal
  const [isRegionModalOpen, setIsRegionModalOpen] = useState(false);

  // Fuse.js and search effect...
  const fuseOptions = useMemo(
    () => ({
      keys: ["unitName", "region", "customer"],
      threshold: 0.35,
    }),
    []
  );
  const fuse = useMemo(() => new Fuse(mockData, fuseOptions), [fuseOptions]);

  useEffect(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      setFilteredData(mockData);
      setSuggestion("");
      return;
    }
    const substringResults = mockData.filter(
      (item) =>
        item.unitName.toLowerCase().includes(query) ||
        item.region.toLowerCase().includes(query) ||
        item.customer.toLowerCase().includes(query)
    );
    if (substringResults.length === mockData.length) {
      setFilteredData(mockData);
      setSuggestion("");
      return;
    }
    const fuseResults = fuse.search(query).map((r) => r.item);
    const finalResults =
      substringResults.length > 0 ? substringResults : fuseResults;
    setFilteredData(finalResults);
    const exactMatchFound = finalResults.some(
      (item) => item.unitName.toLowerCase() === query
    );
    setSuggestion(exactMatchFound ? "" : fuseResults[0]?.unitName || "");
  }, [searchQuery, fuse]);

  // Pagination, sorting, and event handlers remain unchanged...
  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuColumn, setMenuColumn] = useState(null);

  const handleRowClick = (row) => {
    setSelectedRow(row);
    setIsInventoryDrawerOpen(true);
  };

  const handleInventoryDrawerClose = () => {
    setIsInventoryDrawerOpen(false);
    setSelectedRow(null);
  };

  const handleInventorySubmit = () => {
    console.log("Inventory details modified:", selectedRow);
    handleInventoryDrawerClose();
  };

  const handleMoreFiltersClick = () => setIsFilterDrawerOpen(true);
  const handleFilterDrawerClose = () => setIsFilterDrawerOpen(false);
  const handleFilterChange = (key, value) => {
    setUserFilterChoice((prev) => ({ ...prev, [key]: value }));
  };

  const initialFilters = {
    unitType: "None",
    region: "None",
    customer: "None",
    eAndP: "None",
    status: "None",
    manufacturer: "None",
    healthStatus: "None",
    signalStrength: "None",
    timeDuration: "None",
  };
  const [userFilterChoice, setUserFilterChoice] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState({});

  const isAnyFilterAppliedInForm = Object.values(userFilterChoice).some(
    (value) => value !== "None"
  );

  const handleApplyFilters = () => {
    console.log("Filters applied:", userFilterChoice);
    setAppliedFilters(userFilterChoice);
    setIsFilterDrawerOpen(false);
  };

  const handleClearFilters = () => {
    setUserFilterChoice(initialFilters);
    setAppliedFilters({});
  };

  const handleRemoveFilter = (key) => {
    setAppliedFilters((prev) => {
      const updated = { ...prev };
      updated[key] = "None";
      return updated;
    });
  };

  const buildPageNumbers = () => {
    if (TOTAL_PAGES <= 5) {
      return Array.from({ length: TOTAL_PAGES }, (_, i) => i + 1);
    }
    return [1, 2, 3, "...", TOTAL_PAGES - 1, TOTAL_PAGES];
  };

  const goToPage = (p) => {
    if (typeof p === "number" && p >= 1 && p <= TOTAL_PAGES) {
      setPage(p);
    }
  };

  const goToNextPage = () => {
    if (page < TOTAL_PAGES) {
      setPage(page + 1);
    }
  };

  const handleColumnHeaderClick = (colId) => {
    if (sortColumn === colId) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(colId);
      setSortDirection("asc");
    }
  };

  const handleMenuOpen = (event, colId) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuColumn(colId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuColumn(null);
  };

  const handleSortAsc = () => {
    if (menuColumn) {
      setSortColumn(menuColumn);
      setSortDirection("asc");
    }
    handleMenuClose();
  };

  const handleSortDesc = () => {
    if (menuColumn) {
      setSortColumn(menuColumn);
      setSortDirection("desc");
    }
    handleMenuClose();
  };

  const handleFilter = () => {
    console.log("Filter clicked for column:", menuColumn);
    handleMenuClose();
  };

  const handleHideColumn = () => {
    console.log("Hide column:", menuColumn);
    handleMenuClose();
  };

  const handleManageColumns = () => {
    console.log("Manage columns clicked");
    handleMenuClose();
  };

  let currentData = [...filteredData];
  if (sortColumn && sortDirection) {
    currentData.sort((a, b) => {
      const valA = String(a[sortColumn] ?? "");
      const valB = String(b[sortColumn] ?? "");
      return sortDirection === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    });
  }

  return (
    <Box>
      {/* Search / Filters / Export */}
      <Box className="flex flex-col mt-4 space-y-20">
        <Box className="flex flex-wrap items-center justify-between">
          <Box className="flex items-center gap-2">
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                bgcolor:
                  theme.palette.mode === "dark"
                    ? theme.palette.background.paper
                    : {},
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FiSearch className="text-xl" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              onClick={handleMoreFiltersClick}
              sx={{
                textTransform: "none",
                color: "text.primary",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? theme.palette.background.paper
                    : {},
                borderColor:
                  theme.palette.mode === "dark"
                    ? theme.palette.divider
                    : "#BDBDBD",
                "&:hover": {
                  borderColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.text.primary
                      : "#525252",
                },
              }}
              className="flex gap-x-2 h-10"
            >
              <FiFilter className="text-lg" />
              <Typography>More Filters</Typography>
            </Button>
            {Object.values(appliedFilters).some(
              (value) => value !== "None"
            ) && (
              <Typography
                onClick={handleClearFilters}
                sx={{
                  color: "green",
                  cursor: "pointer",
                  "&:hover": { opacity: 0.8 },
                }}
              >
                Clear all
              </Typography>
            )}
          </Box>
          <Box className="flex gap-x-4 mt-2 md:mt-0">
            {/* Placeholder for future buttons, e.g. Export */}
          </Box>
        </Box>
      </Box>

      <Box className="mt-4">
        {Object.values(appliedFilters).some((value) => value !== "None") && (
          <Box className="flex flex-wrap gap-2 mb-4">
            {Object.entries(appliedFilters)
              .filter(([, value]) => value !== "None")
              .map(([key, value]) => (
                <Chip
                  key={key}
                  label={`${value}`}
                  onDelete={() => handleRemoveFilter(key)}
                  deleteIcon={<CloseIcon />}
                  variant="outlined"
                  sx={{
                    fontSize: "0.875rem",
                    padding: "4px 8px",
                    borderRadius: "4px",
                  }}
                />
              ))}
          </Box>
        )}
      </Box>

      <Box className="flex flex-col min-h-[60vh]">
        {suggestion && (
          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
            Did you mean <strong>{suggestion}</strong>?
          </Typography>
        )}

        {/* Main Table */}
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "#282A36" : "white",
            border: (theme) =>
              `1px solid ${
                theme.palette.mode === "dark"
                  ? "#44475A"
                  : "rgba(0, 0, 0, 0.12)"
              }`,
            mb: 2,
          }}
        >
          <Table
            sx={{
              "& .MuiTableCell-root": {
                px: 2,
                py: 1.5,
                color: (theme) => theme.palette.text.primary,
              },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "#44475A" : "#F9FAFB",
                  borderBottom: (theme) =>
                    `1px solid ${
                      theme.palette.mode === "dark" ? "#6272A4" : "#E5E7EB"
                    }`,
                }}
              >
                {columns.map((col) => {
                  const isSorted = sortColumn === col.id;
                  return (
                    <TableCell key={col.id}>
                      <TableSortLabel
                        active={isSorted}
                        direction={isSorted ? sortDirection : "asc"}
                        onClick={() => handleColumnHeaderClick(col.id)}
                        sx={{
                          fontWeight: "bold",
                          color: (theme) =>
                            theme.palette.mode === "dark"
                              ? "#F8F8F2"
                              : "#374151",
                          "&.MuiTableSortLabel-root:hover": {
                            color: (theme) =>
                              theme.palette.mode === "dark"
                                ? "#FFFFFF"
                                : "#1F2937",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: (theme) =>
                              theme.palette.mode === "dark"
                                ? "#BD93F9 !important"
                                : "#9CA3AF !important",
                          },
                        }}
                      >
                        {col.label}
                      </TableSortLabel>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, col.id)}
                        sx={{ color: (theme) => theme.palette.text.primary }}
                      >
                        <MoreVert fontSize="inherit" />
                      </IconButton>
                      <Menu
                        anchorEl={menuAnchorEl}
                        open={Boolean(menuAnchorEl) && menuColumn === col.id}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        sx={{
                          "& .MuiPaper-root": {
                            backgroundColor: (theme) =>
                              theme.palette.mode === "dark"
                                ? "#44475A"
                                : "white",
                            color: (theme) => theme.palette.text.primary,
                          },
                        }}
                      >
                        <MenuItem onClick={handleSortAsc}>Sort ASC</MenuItem>
                        <MenuItem onClick={handleSortDesc}>Sort DESC</MenuItem>
                        <MenuItem onClick={handleFilter}>Filter</MenuItem>
                        <MenuItem onClick={handleHideColumn}>
                          Hide column
                        </MenuItem>
                        <MenuItem onClick={handleManageColumns}>
                          Manage columns
                        </MenuItem>
                      </Menu>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentData.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "#282A36" : "white",
                    "&:hover": {
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "#3A3D4D" : "#F3F4F6",
                    },
                    cursor: "pointer",
                  }}
                  onClick={() => handleRowClick(row)}
                >
                  <TableCell>{row.unitType}</TableCell>
                  <TableCell>{row.unitName}</TableCell>
                  <TableCell>{row.region}</TableCell>
                  <TableCell>{row.customer}</TableCell>
                  <TableCell>{row.eAndP}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>{row.manufacturer}</TableCell>
                  <TableCell>{row.healthStatus}</TableCell>
                  <TableCell>{row.signalStrength}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box className="flex items-center justify-between p-3">
          <Box />
          <Box className="flex items-center gap-2">
            {buildPageNumbers().map((p, idx) => (
              <Button
                key={idx}
                onClick={() => goToPage(p)}
                variant={p === page ? "contained" : "text"}
                sx={{
                  bgcolor:
                    p === page
                      ? (theme) =>
                          theme.palette.mode === "dark" ? "#50FA7B" : "#10B981"
                      : "transparent",
                  color:
                    p === page
                      ? "white"
                      : (theme) => theme.palette.text.primary,
                  "&:hover": {
                    bgcolor:
                      p !== page
                        ? (theme) =>
                            theme.palette.mode === "dark"
                              ? "#3A3D4D"
                              : "#F3F4F6"
                        : "",
                  },
                }}
                disabled={p === "..."}
              >
                {p}
              </Button>
            ))}
          </Box>
          <Box>
            <Button
              onClick={goToNextPage}
              sx={{
                color: (theme) =>
                  theme.palette.mode === "dark" ? "#50FA7B" : "#10B981",
              }}
              className="flex items-center gap-1 !normal-case"
            >
              Next
              <span className="text-xl">&gt;</span>
            </Button>
          </Box>
        </Box>

        {/* Inventory Details Right Drawer */}
        <RightDrawer
          open={isInventoryDrawerOpen}
          onClose={handleInventoryDrawerClose}
          title={selectedRow ? selectedRow.unitName : "Error"}
        >
          <Box className="flex flex-col h-full">
            {/* Logo + Online */}
            <Box className="flex items-center gap-2 mb-4">
              <img
                src={safetyRatLogo}
                alt="Safety Rat Logo"
                className="w-14 h-14 object-contain"
              />
              <Box>
                <Typography variant="h6" className="font-semibold !leading-5">
                  Online
                </Typography>
                <Box className="flex items-center gap-1">
                  <Box className="w-2 h-2 bg-green-500 rounded-full" />
                  <Typography variant="caption" className="text-green-500">
                    Active
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Project GPS Location */}
            <Box className="mb-4">
              <Typography
                variant="subtitle2"
                className="text-gray-500 uppercase text-xs tracking-wide"
              >
                Project GPS Location
              </Typography>
              <Box className="flex items-center gap-1 mt-1">
                <FiLink className="text-gray-500" />
                <Typography variant="body2">74637.5.424</Typography>
              </Box>
            </Box>

            {/* Scheduled Maintenance */}
            <Box className="mb-4">
              <Typography
                variant="subtitle2"
                className="text-gray-500 uppercase text-xs tracking-wide"
              >
                Scheduled Maintenance
              </Typography>
              <Box className="flex items-center gap-1 mt-1">
                <FiCalendar className="text-gray-500" />
                <Typography variant="body2">Friday, Jan 10, 2025</Typography>
              </Box>
              <Box className="flex items-center gap-1">
                <FiClock className="text-gray-500" />
                <Typography variant="body2">1:30 PM - 3:30 PM</Typography>
              </Box>
              <Box className="flex items-center gap-1">
                <FiBell className="text-gray-500" />
                <Typography variant="body2">10 min before</Typography>
              </Box>
            </Box>

            {/* Alerts Buttons */}
            <Box className="mb-4 flex gap-2">
              <Button
                variant="contained"
                className="!bg-blue-500 !text-white !normal-case"
              >
                Manage Alerts
              </Button>
              <Button
                variant="contained"
                className="!bg-green-500 !text-white !normal-case"
              >
                New Alert
              </Button>
            </Box>

            {/* Editable Fields with Add buttons */}
            {/* Name of Device */}
            <Box className="mb-4">
              <Box className="flex items-center justify-between">
                <Typography
                  variant="subtitle2"
                  className="text-gray-500 mb-1 font-semibold"
                >
                  Name of Device <span className="text-red-500">*</span>
                </Typography>
                <Button
                  variant="text"
                  onClick={() => navigate("/edit-unit/new")}
                >
                  Add Device
                </Button>
              </Box>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter device name"
                defaultValue={selectedRow?.unitName || ""}
              />
            </Box>

            {/* Name of Customer */}
            <Box className="mb-4">
              <Box className="flex items-center justify-between">
                <Typography
                  variant="subtitle2"
                  className="text-gray-500 mb-1 font-semibold"
                >
                  Name of Customer <span className="text-red-500">*</span>
                </Typography>
                <Button
                  variant="text"
                  onClick={() => navigate("/create-customer")}
                >
                  Add Customer
                </Button>
              </Box>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter customer name"
                defaultValue={selectedRow?.customer || ""}
              />
            </Box>

            {/* Region as a dropdown with Add button */}
            <Box className="mb-4">
              <Box className="flex items-center justify-between">
                <Typography
                  variant="subtitle2"
                  className="text-gray-500 mb-1 font-semibold"
                >
                  Region <span className="text-red-500">*</span>
                </Typography>
                <Button
                  variant="text"
                  onClick={() => setIsRegionModalOpen(true)}
                >
                  Add Region
                </Button>
              </Box>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedRow?.region || "None"}
                  onChange={(e) =>
                    setSelectedRow((prev) => ({
                      ...prev,
                      region: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="None">None</MenuItem>
                  {regionList?.map((region) => (
                    <MenuItem key={region.id} value={region.name}>
                      {region.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Team Members */}
            <Typography variant="subtitle1" className="font-bold mb-1">
              Team members
            </Typography>
            <Typography variant="body2" className="text-gray-600 mb-3">
              The following are working on this pump.
            </Typography>

            {/* Static team members list */}
            <Box className="border-t border-gray-200">
              {[
                {
                  id: 1,
                  name: "John Doe",
                  email: "jdoe@email.com",
                  initials: "JD",
                  bgColor: "bg-purple-200",
                },
                {
                  id: 2,
                  name: "John Doe",
                  email: "jdoe@email.com",
                  initials: "DW",
                  bgColor: "bg-gray-300",
                },
                {
                  id: 3,
                  name: "John Doe",
                  email: "jdoe@email.com",
                  initials: "JD",
                  bgColor: "bg-pink-200",
                },
              ].map((user) => (
                <Box
                  key={user.id}
                  className="flex items-center gap-2 py-3 border-b border-gray-200 last:border-b-0"
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${user.bgColor} text-gray-800 font-semibold`}
                  >
                    {user.initials}
                  </div>
                  <Box>
                    <Typography variant="body2" className="font-semibold">
                      {user.name}
                    </Typography>
                    <Typography variant="caption" className="text-gray-500">
                      {user.email}
                    </Typography>
                  </Box>
                  <Button
                    variant="text"
                    className="!text-red-500 !ml-auto !normal-case"
                  >
                    Remove
                  </Button>
                </Box>
              ))}

              {/* Action Buttons at the bottom */}
              <Box className="mt-auto flex justify-end gap-2 p-3 border-t border-gray-200">
                <Button variant="outlined" onClick={handleInventoryDrawerClose}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleInventorySubmit}>
                  Modify
                </Button>
              </Box>
            </Box>
          </Box>
        </RightDrawer>

        {/* Filter Drawer */}
        <RightDrawer
          open={isFilterDrawerOpen}
          onClose={handleFilterDrawerClose}
          title="Filter Options"
        >
          <Box className="flex flex-col h-full">
            <Box className="flex flex-col gap-4 overflow-y-auto p-2 flex-grow">
              {[
                {
                  key: "unitType",
                  label: "Unit Type",
                  options: ["None", "E3-TEST-UNIT", "Type A", "Type B"],
                },
                {
                  key: "region",
                  label: "Region",
                  options: ["None", "US", "UK", "AU"],
                },
                {
                  key: "customer",
                  label: "Customer",
                  options: ["None", "Customer A", "Customer B"],
                },
                {
                  key: "eAndP",
                  label: "E & P",
                  options: ["None", "E&P A", "E&P B"],
                },
                {
                  key: "status",
                  label: "Status",
                  options: ["None", "Active", "Disconnected"],
                },
                {
                  key: "manufacturer",
                  label: "Manufacturer",
                  options: ["None", "Digi", "ABC", "XYZ"],
                },
                {
                  key: "healthStatus",
                  label: "Health Status",
                  options: ["None", "Healthy", "Warning", "Critical"],
                },
                {
                  key: "signalStrength",
                  label: "Signal Strength",
                  options: ["None", "Strong", "Weak", "Moderate"],
                },
                {
                  key: "timeDuration",
                  label: "Time Duration",
                  options: ["None", "12 months", "30 days", "7 days", "24h"],
                },
              ].map((filter, index) => (
                <Box
                  key={index}
                  className="flex items-center justify-between gap-4"
                >
                  <Typography
                    variant="subtitle2"
                    className="text-gray-500 font-semibold"
                  >
                    {filter.label}
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                      value={userFilterChoice[filter.key]}
                      onChange={(e) =>
                        handleFilterChange(filter.key, e.target.value)
                      }
                    >
                      {filter.options.map((option, idx) => (
                        <MenuItem key={idx} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              ))}
            </Box>
            <Box className="p-3 border-t border-gray-300 flex justify-between gap-2">
              {isAnyFilterAppliedInForm && (
                <Button
                  variant="outlined"
                  sx={{ flex: 1, color: "black", borderColor: "gray" }}
                  className="!normal-case"
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </Button>
              )}
              <Button
                fullWidth={!isAnyFilterAppliedInForm}
                variant="contained"
                sx={{ bgcolor: "#22C55E", color: "white", flex: 1 }}
                className="!normal-case"
                onClick={handleApplyFilters}
              >
                Apply Filters
              </Button>
            </Box>
          </Box>
        </RightDrawer>
      </Box>

      {/* Render the Add Region modal */}
      <AddRegionModal
        open={isRegionModalOpen}
        onClose={() => setIsRegionModalOpen(false)}
        onAddRegion={async (regionData) => {
          try {
            await axios.post("http://localhost:3000/api/regions", regionData, {
              withCredentials: true,
            });
            // await apiHandler.post("/api/regions", regionData);
            setIsRegionModalOpen(false);
            window.location.reload(); // Or update state without reload
          } catch (error) {
            console.error("Error adding region:", error);
          }
        }}
        managers={managers}
      />
    </Box>
  );
};

InventoryComponent.propTypes = {
  searchQuery: PropTypes.string,
};

export default InventoryComponent;
